import React, { useState, useEffect } from 'react'
import { Trans } from 'react-i18next'
import useQuery from 'hooks/useQuery'

import useTranslation from 'hooks/useTranslation'
import useRequestState from 'hooks/useRequestState'
import useAuth from 'hooks/useAuth'

import { verifyOtp, sendOtp } from 'services/auth'
import { logLogin } from 'services/analytics/events'

import Button from 'components/Button'
import Icon from 'components/Icon'
import OTPInput from 'components/OTPInput'
import { Heading, Text } from 'components/Typography'
import Countdown from 'components/Countdown'
import { Alert } from 'antd'

import styles from './OTPForm.module.scss'

import { LoginQuery } from 'types/query'
import { Routes } from 'constants/routes'
import { useHistory } from 'react-router-dom'

const OTP_COUNTDOWN = 60

type Props = {
  email: string
}

const OTPForm = (props: Props) => {
  const { email } = props
  const [otp, setOtp] = useState('')
  const [countdown, setCountdown] = useState(OTP_COUNTDOWN)
  const { [LoginQuery.redirect]: query } = useQuery()
  const redirectRisk = query === 'risk'
  const { t, copy } = useTranslation()
  const state = useRequestState()

  const router = useHistory()

  const { login } = useAuth()

  useEffect(() => {
    const timer = setTimeout(() => {
      if (countdown) setCountdown((c) => c - 1)
    }, 1000)

    return () => {
      clearTimeout(timer)
    }
  }, [countdown])

  const handleSubmit = async () => {
    try {
      state.start()
      const { data } = await verifyOtp({ email, otp })
      const token: Token = {
        access: data.access_token,
        refresh: data.refresh,
      }
      login(token)
      if (redirectRisk) {
        router.push(Routes.risk)
      }
      await logLogin({ email })
    } catch (e) {
      state.setError(e.response?.data?.detail)
      state.end()
    }
  }

  const handleResend = async () => {
    try {
      state.start()
      setCountdown(OTP_COUNTDOWN)
      const { data } = await sendOtp({ email })
      state.setSuccess(data?.detail)
    } catch (e) {
      state.setError(e.response?.data?.detail)
      state.end()
    }
  }

  return (
    <div className={styles.otpFormContainer}>
      <Heading size="2xl" className={styles.heading}>
        {t(copy.Containers.OTPForm.heading)}
      </Heading>
      <Text paragraph className={styles.subheading}>
        {t(copy.Containers.OTPForm.subheading)}
      </Text>
      {state.error && (
        <Alert
          message={t(copy.Containers.OTPForm.alertInvalidOtp)}
          type="error"
          className={styles.alert}
          icon={<Icon name="alert" type="danger" />}
          showIcon
        />
      )}
      {state.success && countdown !== 0 && (
        <Alert
          message={t(copy.Containers.OTPForm.alertResentOtp)}
          type="success"
          className={styles.alert}
          icon={<Icon name="paper-plane" type="success" />}
          showIcon
        />
      )}
      <OTPInput
        autoFocus
        isNumberInput
        length={6}
        onChangeOTP={(val) => setOtp(val)}
        className={styles.otpContainer}
        error={state.error}
      />
      <Countdown
        seconds={countdown}
        renderCountdown={(countdown) => (
          <Trans i18nKey={copy.Containers.OTPForm.countdown}>
            <Text type="danger" strong>
              {{ countdown }}
            </Text>
          </Trans>
        )}
        renderElapsed={() => (
          <div className={styles.otpResend}>
            <Text>{t(copy.Containers.OTPForm.receive)}</Text>
            <Button type="link" onClick={handleResend}>
              <Icon name="redo" type="primary" />
              <Text type="primary">{t(copy.Containers.OTPForm.resend)}</Text>
            </Button>
          </div>
        )}
      />
      <Button
        block
        className={styles.button}
        disabled={otp.length < 6}
        onClick={handleSubmit}
      >
        {t(copy.Containers.OTPForm.verify)}
      </Button>
      <Text paragraph>{t(copy.Containers.OTPForm.mail)}</Text>
      <div className={styles.mail}>
        <a href="https://mail.google.com/" rel="noreferrer" target="_blank">
          <Icon name="google" size="md" type="primary" />
          <span>Gmail</span>
        </a>
      </div>
      <div className={styles.mail}>
        <a href="https://mail.yahoo.com/" rel="noreferrer" target="_blank">
          <Icon name="yahoo" size="lg" type="primary" />
          <span>Yahoo</span>
        </a>
      </div>
    </div>
  )
}

export default OTPForm
